import { ChangeDetectionStrategy, Component, computed, input, signal } from "@angular/core";
import { DecimalPipe } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatProgressBar } from "@angular/material/progress-bar";
import { PageEvent } from "@angular/material/paginator";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";
import { PaginatorComponent } from "@sxm-content-creators/tools-ds";
import { PollResultItem } from "../../../models/poll.model";
import { rankPollResults } from "./poll-results-table.util";

@Component({
    selector: "app-poll-results-table",
    templateUrl: "./poll-results-table.component.html",
    styleUrl: "./poll-results-table.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [DecimalPipe, MatFormFieldModule, MatProgressBar, MatSelectModule, MatTableModule, PaginatorComponent]
})
export class PollResultsTableComponent {
    readonly results = input.required<PollResultItem[]>();
    readonly displayIsWriteInColumn = input<boolean>(false);
    readonly displayPercentageColumn = input<boolean>(false);
    readonly showFooter = input<boolean>(false);
    readonly showTotalSelectionsInFooter = input<boolean>(false);
    readonly searchQuery = input<string>("");
    readonly writeInOnly = input<boolean>(false);

    readonly pageIndex = signal(0);
    readonly pageSize = signal(20);

    readonly rankedResults = computed(() => rankPollResults(this.results()));

    readonly filteredResults = computed(() => {
        const query = this.searchQuery().trim().toLowerCase();
        const writeInOnly = this.writeInOnly();
        return this.rankedResults().filter(item => {
            if (writeInOnly && !item.isWriteIn) return false;
            return !query || item.label.toLowerCase().includes(query);
        });
    });

    readonly clampedPageIndex = computed(() => {
        const rowCount = this.filteredResults().length;
        const pageSize = this.pageSize();
        const maxPageIndex = Math.max(0, Math.ceil(rowCount / pageSize) - 1);
        return Math.min(this.pageIndex(), maxPageIndex);
    });

    readonly pagedResults = computed(() => {
        const rows = this.filteredResults();
        if (!this.showFooter()) return rows;

        const pageSize = this.pageSize();
        const start = this.clampedPageIndex() * pageSize;
        return rows.slice(start, start + pageSize);
    });

    readonly tableColumns = computed(() => {
        const columns = ["rank", "selection", "votes"];
        if (this.displayPercentageColumn()) columns.push("percentage");
        if (this.displayIsWriteInColumn()) columns.push("is-write-in");

        // Empty, unsized column appended last so it — not a real content column — absorbs any leftover table width
        columns.push("filler");

        return columns;
    });

    onPageChange(event: PageEvent): void {
        this.pageIndex.set(event.pageIndex);
    }

    onPageSizeChange(pageSize: number): void {
        this.pageSize.set(pageSize);
        this.pageIndex.set(0);
    }

    trackByItemId(_index: number, item: PollResultItem): string {
        return item.itemId;
    }

    protected readonly pageSizeOptions = [20, 50, 100];
}
